import React, { useContext } from "react";
import {
  Avatar,
  Badge,
  Button,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ForumIcon from "@material-ui/icons/Forum";
import { format, isSameDay, parseISO } from "date-fns";

import { useHistory, useParams } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { colorFromName, getInitials } from "./chatColors";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
    borderRadius: 0,
    backgroundColor: theme.palette.boxlist,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.tabHeaderBackground,
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    flex: "none",
  },
  headerTitle: {
    fontWeight: 500,
    // Sem isso o Typography cai no "color: initial" do MUI v4 (preto do browser),
    // que fica ilegível sobre o cabeçalho escuro do tema dark.
    color: theme.palette.text.primary,
  },
  newButton: {
    // Azul do sistema fixo: no tema escuro palette.primary.main vira branco,
    // então usar color="primary" deixaria o botão branco.
    backgroundColor: "#00aeef",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#0095cc",
    },
  },
  chatList: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    ...theme.scrollbarStyles,
  },
  listItem: {
    cursor: "pointer",
    // Faixa lateral verde na conversa aberta. box-shadow (e nao border-left) para
    // nao deslocar o conteudo do item, mesma tecnica da sidebar.
    "&.Mui-selected": {
      boxShadow: "inset 3px 0 0 #4caf50",
    },
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: "10%",
    color: "#fff",
    fontWeight: 500,
  },
  avatarWrapper: {
    minWidth: 60,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  title: {
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    // Mesmo motivo do headerTitle: sem cor explícita o Typography fica preto
    // (color: initial do MUI v4) e some no fundo escuro.
    color: theme.palette.text.primary,
  },
  time: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    flex: "none",
  },
  lastMessageRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  lastMessage: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  badge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#f44336",
      color: "#fff",
    },
    marginRight: 6,
  },
  emptyState: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

export default function ChatList({ chats, handleSelectChat, handleNewChat }) {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const { id } = useParams();

  const goToMessages = async (chat) => {
    if (unreadMessages(chat) > 0) {
      try {
        await api.post(`/chats/${chat.id}/read`, { userId: user.id });
      } catch (err) {}
    }

    if (id !== chat.uuid) {
      history.push(`/chats/${chat.uuid}`);
    }
    handleSelectChat(chat);
  };

  const unreadMessages = (chat) => {
    const currentUser = chat.users?.find((u) => u.userId === user.id);
    return currentUser?.unreads || 0;
  };

  // Mesma regra do item de ticket: hora no dia corrente, data completa no resto.
  const formatTime = (value) => {
    if (!value) return "";
    try {
      const date = parseISO(value);
      return isSameDay(date, new Date())
        ? format(date, "HH:mm")
        : format(date, "dd/MM/yyyy");
    } catch (err) {
      return "";
    }
  };

  if (!Array.isArray(chats) || chats.length === 0) {
    return (
      <div className={classes.mainContainer}>
        <div className={classes.header}>
          <Typography variant="subtitle1" className={classes.headerTitle}>
            {i18n.t("internalChat.list.title")}
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            className={classes.newButton}
            onClick={handleNewChat}
          >
            {i18n.t("internalChat.list.new")}
          </Button>
        </div>
        <div className={classes.emptyState}>
          <ForumIcon style={{ fontSize: 48, opacity: 0.4 }} />
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 8 }}
          >
            {i18n.t("internalChat.list.empty")}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.mainContainer}>
      <div className={classes.header}>
        <Typography variant="subtitle1" className={classes.headerTitle}>
          {i18n.t("internalChat.list.title")}
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          className={classes.newButton}
          onClick={handleNewChat}
        >
          {i18n.t("internalChat.list.new")}
        </Button>
      </div>
      <div className={classes.chatList}>
        <List style={{ paddingTop: 0 }}>
          {chats.map((chat) => {
            const unreads = unreadMessages(chat);
            return (
              <React.Fragment key={chat.id}>
                <ListItem
                  onClick={() => goToMessages(chat)}
                  className={classes.listItem}
                  selected={chat.uuid === id}
                  button
                  dense
                >
                  <ListItemAvatar className={classes.avatarWrapper}>
                    <Avatar
                      className={classes.avatar}
                      style={{ backgroundColor: colorFromName(chat.title) }}
                    >
                      {getInitials(chat.title)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    disableTypography
                    primary={
                      <div className={classes.titleRow}>
                        <Typography
                          component="span"
                          variant="body2"
                          className={classes.title}
                        >
                          {chat.title}
                        </Typography>
                        <span className={classes.time}>
                          {formatTime(chat.updatedAt)}
                        </span>
                      </div>
                    }
                    secondary={
                      <div className={classes.lastMessageRow}>
                        <Typography
                          component="span"
                          className={classes.lastMessage}
                        >
                          {chat.lastMessage}
                        </Typography>
                        {unreads > 0 && (
                          <Badge
                            className={classes.badge}
                            badgeContent={unreads}
                          />
                        )}
                      </div>
                    }
                  />
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            );
          })}
        </List>
      </div>
    </div>
  );
}
